<template>
	<section aria-label="Question Summary" id="feedback" class="feedback-summary row" tabindex="-1">
		<template v-if="isGraded && !hasUnsaved">
			<div aria-live="polite">
				<div class="summary col-md-6">
					<!-- short answer and numeric -->
					<div v-if="[ 'S','N' ].indexOf( activeQuestion.QUESTION_TYPE ) > -1">
						<p v-for="ans in studentAnswer" :key="ans.TEST_QUESTION_ANSWER_TEXT">
							{{ $t('MAIN_CONTENT.QuestionSummary.yourAnswer') }} <b>
								{{ ans.TEST_QUESTION_ANSWER_TEXT }}
							</b> {{ $t('MAIN_CONTENT.QuestionSummary.is') }} 
							<span v-if="isCorrect( ans )">
								<b class="color-correct-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.correct') }}
								</b>
							</span>
							<span v-else-if="ans.POINTS_EARNED > 0">
								<b class="color-correct-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.partiallyCorrect') }}
								</b>
							</span>
							<span v-else>
								<b class="color-incorrect-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.incorrect') }}
								</b>
							</span>
						</p>
					</div>
					<!-- multiple choice, true/false -->
					<div v-else-if="[ 'C','T' ].indexOf( activeQuestion.QUESTION_TYPE ) > -1">
						<p v-for="ans in studentAnswer" :key="ans.ANSWER_RID">
							{{ $t('MAIN_CONTENT.QuestionSummary.yourAnswer') }} <b>
								{{ getAnswerChoiceNumber( ans.ANSWER_RID ) }}
								</b> {{ $t('MAIN_CONTENT.QuestionSummary.is') }}
							<span v-if="ans.POINTS_EARNED > 0">
								<b class="color-correct-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.correct') }}
								</b>
							</span>
							<span v-else>
								<b class="color-incorrect-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.incorrect') }}
								</b>
							</span>
						</p>
					</div>
					<!-- matching -->
					<div v-else-if="[ 'M' ].indexOf( activeQuestion.QUESTION_TYPE ) > -1">
						<p v-for="(subQ) in studentAnswer" :key="subQ.TEST_QUESTION_RID">
							{{ $t('MAIN_CONTENT.QuestionSummary.yourAnswer') }} 
							<b v-html="getMatchText( subQ.ANSWER_RID )"></b> 
							{{ $t('MAIN_CONTENT.QuestionSummary.is') }}
							<span v-if="subQ.ANSWER_CORRECT_FLG">
								<b class="color-correct-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.correct') }}
								</b>
							</span>
							<span v-else-if="subQ.POINTS_EARNED > 0">
								<b class="color-correct-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.partiallyCorrect') }}
								</b>
							</span>
							<span v-else>
								<b class="color-incorrect-lighten">
									{{ $t('MAIN_CONTENT.QuestionSummary.incorrect') }}
								</b>
							</span>
						</p> 
					</div>

					<div>
						<span class="pill pill-dark">
							<strong>{{ pointsEarned }}</strong> {{ $t('MAIN_CONTENT.QuestionSummary.ptsEarned') }}
						</span>
					</div>
				</div>
				<div
						v-if="isGraded && !hasUnsaved"
						class="feedback col-md-6">
					<div
							v-if="hasFeedback"
							class="speech-bubble">
						<div class="arrow bottom" />
						<div
								v-for="( ans, index ) in studentAnswer"
								:key="index">
							<p v-if="!!ans.ANSWER_SPECIFIC_FEEDBACK && ans.ANSWER_SPECIFIC_FEEDBACK.length > 0 && (index === 0 || (studentAnswer[index-1].ANSWER_SPECIFIC_FEEDBACK !== ans.ANSWER_SPECIFIC_FEEDBACK))" v-html="ans.ANSWER_SPECIFIC_FEEDBACK"></p>
							<p v-if="index === studentAnswer.length -1 && !!ans.GENERAL_QUESTION_FEEDBACK && ans.GENERAL_QUESTION_FEEDBACK.length > 0" v-html="ans.GENERAL_QUESTION_FEEDBACK"></p>
							<p v-if="index === studentAnswer.length -1 && !!ans.QUESTION_GRADED_COMMENTS && ans.QUESTION_GRADED_COMMENTS.length > 0" v-html="ans.QUESTION_GRADED_COMMENTS"></p>
						</div>
					</div>
				</div>
			</div>
		</template>
	</section>
</template>

<script>
	import { max } from 'moment';

	import { mapState,mapGetters } from "vuex";
	export default {
    data (){
        return{
            T:  ["True","False"]
        };
    },
        
    computed: {
        ...mapState( {
            testPreview: state =>{
                return state.testTaker.testPreview;
            },
            activeQuestion: state =>{
                return state.testTaker.activeQuestion;
            },
            // object of all answered questions, keyed by questionID
            studentAnswers: state =>{
                return state.testTaker.testPreview.STUDENT_ATTEMPT.ANSWERS;
            },
            unsavedAnswerIds: state =>{
                return Object.keys( state.testTaker.unsavedChanges );
            }
        } ),
        ...mapGetters( [ "primaryQuestions" ] ),
        hasUnsaved (){
            return this.unsavedAnswerIds.indexOf( this.activeQuestion.TEST_QUESTION_RID.toString() ) > -1;
        },
        studentAnswer (){
            return this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] ? this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] : [];
        },
        hasFeedback (){
            return this.studentAnswer.filter( ans =>{
                return ans.GENERAL_QUESTION_FEEDBACK && ans.GENERAL_QUESTION_FEEDBACK.trim().length > 0 
                || ans.ANSWER_SPECIFIC_FEEDBACK && ans.ANSWER_SPECIFIC_FEEDBACK.trim().length > 0
                || ans.QUESTION_GRADED_COMMENTS && ans.QUESTION_GRADED_COMMENTS.trim().length > 0; 
            } ).length > 0;
        },
        activeQuestionIndex (){
            if( ! this.activeQuestion ) return -1;
            return this.primaryQuestions.map( q=>{
                return q.TEST_QUESTION_RID; 
            } ).indexOf( this.activeQuestion.TEST_QUESTION_RID );
        },
        activeQuestionAnswers (){
            return this.testPreview.ANSWERS.filter( ans =>{
                return ans.TEST_QUESTION_RID === this.activeQuestion.TEST_QUESTION_RID; 
            } );
        },
        pointsEarned (){
            let total = 0;
            let wrongAnswerSel = false;
            this.studentAnswer.forEach( ans =>{
                total = Number(total) + Number(ans.POINTS_EARNED);
                wrongAnswerSel += Number(ans.POINTS_EARNED) < 0;
            } );

            if(!this.activeQuestion.ALLOW_NEGATIVE_TOTAL_POINTS_FLG && total < 0){
                total = 0;
            }
            /* If multiple choice and user picks all answer they get cancelled out */
            if(this.activeQuestion.QUESTION_TYPE === "C" && this.studentAnswer.length === this.activeQuestionAnswers.length
                && wrongAnswerSel){
                total = 0;
            }
            return Math.min(Math.round(((total + Number.EPSILON) * 100) / 100), this.activeQuestion.TOTAL_POINTS);
        },
        isGraded(){
            return this.studentAnswer && this.studentAnswer.filter( ans =>{
                return ans.QUESTION_GRADED_STATUS === "A" || ans.QUESTION_GRADED_STATUS === "M"; 
            } ).length > 0;
        }
    },
        
    methods: {
        getMatchText(answerRid){
            let answer = this.testPreview.ANSWERS.find((itm)=>{
                return Number(answerRid) === Number(itm.ANSWER_RID)
            })

				if(answer) {
					return answer.ANSWER_TEXT;
				} else {
					return "";
				}
			},
			// compares answer against answer key
			isCorrect ( answer ){
				if( ! this.isGraded ) return;
				return answer.ANSWER_CORRECT_FLG;
			},
			// returns either letter, number or roman numeral for the answer choice
			getAnswerChoice ( index ){
				if(this.activeQuestion.QUESTION_TYPE === "T"){
					return this.T[ index ];
				}
				return this.numToLetters(index+1);
			},
			numToLetters(v) {
				let result = [];
				let accum = Number(v);
				if (accum < 1) {
					return "";
				}
				while (accum>0) {
					let rem = (accum-1)%26;
					result.splice(0,0,String.fromCharCode(65+rem));
					accum = (accum - rem - 1)/26;
				}
				return result.join("");
			},
			getAnswerChoiceNumber ( answerId ){
				let index = this.activeQuestionAnswers.map( ans => {
					return ans.ANSWER_RID; 
				} ).indexOf( answerId );
				return this.getAnswerChoice( index );
			},
		}
	};
</script>
